import { createJSONStorage, type StateStorage } from 'zustand/middleware';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';

export type UserPersistContext = {
  userId: string | null;
  isAnonymous: boolean;
  usingSignInAs: boolean;
};

const anonymousContext: UserPersistContext = {
  userId: null,
  isAnonymous: true,
  usingSignInAs: false,
};

let persistContext: UserPersistContext = { ...anonymousContext };

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

/** Update the active user for sample/substrate filter persistence (call when auth changes). */
export function setUserPersistContext(ctx: UserPersistContext): void {
  persistContext = ctx;
}

export function getUserPersistContext(): UserPersistContext {
  return persistContext;
}

export function resetUserPersistContext(): void {
  persistContext = { ...anonymousContext };
}

/** Signed-in, non-anonymous — may read user bucket (falls back to legacy shared key). */
export function canReadUserScopedStorage(): boolean {
  return Boolean(persistContext.userId && !persistContext.isAnonymous);
}

/** Persist writes for signed-in users who are not impersonating. */
export function canWriteUserScopedStorage(): boolean {
  return canReadUserScopedStorage() && !persistContext.usingSignInAs;
}

function userScopedLogicalKey(logicalKey: string): string | null {
  if (!persistContext.userId) return null;
  return `users/${persistContext.userId}/${logicalKey}`;
}

/**
 * Reads JSON from the current user's storage slot, migrating from the legacy shared key once.
 */
export function readUserScopedJson(logicalKey: string): string | null {
  if (!canReadUserScopedStorage()) return null;

  const userKey = userScopedLogicalKey(logicalKey);
  if (!userKey) return null;

  const userValue = scopedLocalStorage.getItem(userKey);
  if (userValue != null) return userValue;

  const legacy = scopedLocalStorage.getItem(logicalKey);
  if (legacy != null && canWriteUserScopedStorage()) {
    scopedLocalStorage.setItem(userKey, legacy);
  }
  return legacy;
}

export function writeUserScopedJson(logicalKey: string, value: string): void {
  if (!canWriteUserScopedStorage()) return;
  const userKey = userScopedLogicalKey(logicalKey);
  if (!userKey) return;
  scopedLocalStorage.setItem(userKey, value);
}

export function removeUserScopedJson(logicalKey: string): void {
  if (!canWriteUserScopedStorage()) return;
  const userKey = userScopedLogicalKey(logicalKey);
  if (!userKey) return;
  scopedLocalStorage.removeItem(userKey);
}

const userScopedStateStorage: StateStorage = {
  getItem: (name) => readUserScopedJson(name),
  setItem: (name, value) => writeUserScopedJson(name, value),
  removeItem: (name) => removeUserScopedJson(name),
};

/** Zustand persist adapter: per-user keys under `users/{id}/`, legacy migration on read. */
export function createUserScopedJSONStorage() {
  return createJSONStorage(() => (typeof window === 'undefined' ? noopStorage : userScopedStateStorage));
}
